/**
 * Shared Confirmation Dialog Component
 * Standard modal for confirming destructive or important actions
 */

import React from 'react';
import { Modal, Button, Space, theme } from 'antd';
import { ExclamationCircleOutlined, CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { controlHeightTokens } from '@/styles/tokens';

export interface ConfirmationDialogProps {
  /** Dialog visibility state */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Confirm action handler */
  onConfirm: () => void;
  /** Dialog title */
  title: string;
  /** Dialog message/description */
  message: string | React.ReactNode;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Dialog type affects icon and confirm button color */
  type?: 'danger' | 'warning' | 'info' | 'success';
  /** Loading state for confirm button */
  loading?: boolean;
  /** Modal width */
  width?: number;
  /** Show icon */
  showIcon?: boolean;
}

/**
 * Confirmation dialog for important actions
 *
 * @example
 * ```tsx
 * const [showDialog, setShowDialog] = useState(false);
 *
 * <ConfirmationDialog
 *   open={showDialog}
 *   onClose={() => setShowDialog(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Vendor"
 *   message="Are you sure you want to delete this vendor? This action cannot be undone."
 *   type="danger"
 *   confirmText="Delete"
 *   loading={isDeleting}
 * />
 * ```
 */
export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger',
  loading = false,
  width = 480,
  showIcon = true,
}) => {
  const { token } = theme.useToken();

  // Icon configuration based on type
  const iconConfig = {
    danger: {
      icon: <ExclamationCircleOutlined style={{ fontSize: token.fontSizeHeading3, color: token.colorError }} />,
      buttonType: 'primary' as const,
      buttonDanger: true,
    },
    warning: {
      icon: <ExclamationCircleOutlined style={{ fontSize: token.fontSizeHeading3, color: token.colorWarning }} />,
      buttonType: 'primary' as const,
      buttonDanger: false,
    },
    info: {
      icon: <InfoCircleOutlined style={{ fontSize: token.fontSizeHeading3, color: token.colorInfo }} />,
      buttonType: 'primary' as const,
      buttonDanger: false,
    },
    success: {
      icon: <CheckCircleOutlined style={{ fontSize: token.fontSizeHeading3, color: token.colorSuccess }} />,
      buttonType: 'primary' as const,
      buttonDanger: false,
    },
  };

  const config = iconConfig[type];

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={width}
      footer={null}
      centered
      closable={!loading}
      maskClosable={!loading}
    >
      <div className="tw-flex tw-gap-4">
        {/* Icon */}
        {showIcon && (
          <div className="tw-flex-shrink-0 tw-pt-1">
            {config.icon}
          </div>
        )}

        {/* Content */}
        <div className="tw-flex-1">
          {/* Title */}
          <h3
            className="tw-mb-2"
            style={{
              fontSize: token.fontSizeLG,
              fontWeight: token.fontWeightStrong,
              color: token.colorText,
              margin: 0,
              marginBottom: token.marginXS,
            }}
          >
            {title}
          </h3>

          {/* Message */}
          <div
            style={{
              fontSize: token.fontSize,
              color: token.colorTextSecondary,
              marginBottom: token.marginLG,
              lineHeight: 1.6,
            }}
          >
            {message}
          </div>

          {/* Actions */}
          <Space size="middle" className="tw-w-full tw-justify-end">
            <Button
              onClick={onClose}
              disabled={loading}
              style={{
                height: `${controlHeightTokens.controlHeightXL}px`,
                fontSize: token.fontSize,
              }}
            >
              {cancelText}
            </Button>
            <Button
              type={config.buttonType}
              danger={config.buttonDanger}
              onClick={handleConfirm}
              loading={loading}
              style={{
                height: `${controlHeightTokens.controlHeightXL}px`,
                fontSize: token.fontSize,
              }}
            >
              {confirmText}
            </Button>
          </Space>
        </div>
      </div>
    </Modal>
  );
};

/**
 * Hook for managing confirmation dialog state
 *
 * @example
 * ```tsx
 * const deleteDialog = useConfirmationDialog();
 *
 * <Button onClick={() => deleteDialog.open(vendor)}>Delete</Button>
 *
 * <ConfirmationDialog
 *   open={deleteDialog.isOpen}
 *   onClose={deleteDialog.close}
 *   onConfirm={() => {
 *     handleDelete(deleteDialog.data);
 *     deleteDialog.close();
 *   }}
 *   title="Delete Vendor"
 *   message={`Delete ${deleteDialog.data?.name}?`}
 * />
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useConfirmationDialog = <T = unknown>() => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [data, setData] = React.useState<T | null>(null);

  const open = (itemData?: T) => {
    if (itemData) {
      setData(itemData);
    }
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    // Clear data after animation completes
    setTimeout(() => setData(null), 300);
  };

  return {
    isOpen,
    data,
    open,
    close,
  };
};
